import React from 'react'

import PreventiveVersionLink from '../PreventiveVersionLink'
import VersionItemOptions from '../VersionItemOptions'
import StarVersion, { Star } from '../StarVersion'

import { ShareWithoutVersion } from '../../ShareVersionContext/ShareVersionContext'

import { VersionItem } from './ShareVersions.styles'

import { useDuplicateShare } from 'modules/shares/hooks/useDuplicateShare'

import {
  VersionDeletedFragment,
  VersionListItemFragment,
  VersionUnavailableFragment,
} from '@sketch/gql-types'

type Version =
  | VersionDeletedFragment
  | VersionUnavailableFragment
  | VersionListItemFragment

interface ShareVersionsItemProps {
  version: Version
  share: Pick<
    ShareWithoutVersion,
    | 'identifier'
    | 'name'
    | 'type'
    | 'userCanEdit'
    | 'userCanInspect'
    | 'publicAccessLevel'
    | 'workspace'
  >
  position?: number
  isLatestVersion: boolean
  isLatestStared: boolean
  isSingleVersion: boolean
  isVersionSelected: boolean
  hideDescription?: boolean
}

export const ShareVersionsItem = (props: ShareVersionsItemProps) => {
  const {
    version,
    position,
    share,
    isLatestVersion,
    isLatestStared,
    isSingleVersion,
    isVersionSelected,
    hideDescription,
  } = props

  const [duplicateShare] = useDuplicateShare({
    share,
    versionIdentifier: version.identifier,
    workspaceIdentifier: share.workspace.identifier,
  })

  const authorName = version.creator.name || ''

  if (
    version.__typename === 'VersionDeleted' ||
    version.__typename === 'VersionUnavailable'
  ) {
    return (
      <VersionItem
        order={position}
        authorName={authorName}
        creationDate={version.createdAt}
        deleted={version.__typename === 'VersionDeleted'}
      />
    )
  }

  const starred = version.kind === 'PUBLISHED'
  const renderStatus = version.document?.renderStatus || undefined

  let star: React.ReactNode | null = null
  if (renderStatus === 'FINISHED' && share.userCanEdit) {
    star = (
      <StarVersion
        starred={starred}
        isLibrary={share.type === 'LIBRARY'}
        isNewLatest={isLatestStared}
        versionIdentifier={version.identifier}
        versionDescription={version.description || undefined}
        shareIdentifier={share.identifier}
      />
    )
  } else if (starred) {
    star = <Star starred />
  }

  const description = hideDescription ? undefined : version.description

  return (
    <PreventiveVersionLink
      key={version.identifier}
      versionShortId={version.shortId}
      isLatestVersion={isLatestVersion}
      isActive={match => !!match || isVersionSelected}
    >
      <VersionItem
        order={position}
        authorName={authorName}
        creationDate={version.createdAt}
        description={description || undefined}
        renderStatus={renderStatus}
        starred={starred}
        optionsMenu={() => (
          <VersionItemOptions
            version={version}
            isDocumentLibrary={share.type === 'LIBRARY'}
            isLatestStaredVersion={isLatestStared}
            isSingleVersion={isSingleVersion}
            userCanEdit={share.userCanEdit}
            userCanInspect={share.userCanInspect}
            shareIdentifier={share.identifier}
            shareName={share.name}
            shareWorkspaceIdentifier={share.workspace.identifier}
            sharePublicAccess={share.publicAccessLevel}
            isVersionSelected={isVersionSelected}
            onDuplicateClick={duplicateShare}
          />
        )}
        star={star ? () => star : undefined}
        $starred={starred}
      />
    </PreventiveVersionLink>
  )
}
